import { WarningCircle } from "@phosphor-icons/react";
import { cva, type VariantProps } from "class-variance-authority";
import { clsx } from "clsx";

import ProfileImage, {
  type ProfileImageProps,
} from "common/components/ui/ProfileImage";
import Skeleton from "common/components/ui/Skeleton";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "common/components/ui/Tooltip";
import { TooltipPortal } from "common/components/ui/Tooltip/Tooltip";
import { useSelectedWorkspaceContext } from "common/context/SelectedWorkspaceContext";
import useWorkspaceAccounts from "common/datahooks/useWorkspaceAccounts";

import type { JSX } from "react";

const variants = cva(
  "flex items-center gap-x-1 rounded-full py-0.5 pl-1 pr-2 text-nowrap [&_svg]:-mr-1",
  {
    variants: {
      size: {
        sm: "gap-x-1 text-button-xxs [&_svg]:size-4",
        md: "gap-x-1 text-button-xs [&_svg]:size-5",
        lg: "gap-x-1.5 text-button-xs [&_svg]:size-6",
      },
      variant: {
        purple: "bg-purple-50",
        gray: "bg-black-100 text-black-400",
        disabled: "bg-black-100 text-black-400",
      },
    },
  },
);

interface LinkedInAccountsLabelProps
  extends Pick<VariantProps<typeof variants>, "size"> {
  accountIds: string[];
  rightComponent?: JSX.Element;
  isDisabled?: boolean; // This is only used for disabled MessageField in Inbox (single account)
}

const profileImageSizes: Record<
  LinkedInAccountsLabelProps["size"],
  ProfileImageProps["size"]
> = {
  sm: "3xs",
  md: "2xs",
  lg: "xs",
};

const tagHeights: Record<LinkedInAccountsLabelProps["size"], string> = {
  sm: "h-5",
  md: "h-6",
  lg: "h-7",
};

export default function LinkedInAccountsTag({
  accountIds,
  size = "md",
  rightComponent = null,
  isDisabled = false,
}: LinkedInAccountsLabelProps) {
  const { id: workspaceId } = useSelectedWorkspaceContext();
  const {
    workspaceAccounts,
    isLoadingWorkspaceAccounts,
    workspaceAccountsError,
  } = useWorkspaceAccounts(workspaceId);

  if (isLoadingWorkspaceAccounts) {
    return (
      <Skeleton className={clsx("flex w-24 rounded-full", tagHeights[size])} />
    );
  }
  if (workspaceAccountsError) {
    return (
      <div
        className={clsx(
          tagHeights[size],
          variants({
            size,
            variant: "disabled",
          }),
        )}
      >
        <ProfileImage size={profileImageSizes[size]} src={undefined} />
        Accounts error
        <WarningCircle weight="fill" />
      </div>
    );
  }

  if (accountIds.length === 1) {
    const {
      picture_url: pictureUrl,
      full_name: fullName,
      license,
      type,
    } = workspaceAccounts.find(({ id }) => id === accountIds[0]) || {};

    let variant: VariantProps<typeof variants>["variant"];

    if (isDisabled) {
      variant = "disabled";
    } else {
      variant = license || type === "rental" ? "purple" : "gray";
    }

    return (
      <Tooltip>
        <TooltipTrigger
          className={clsx(
            tagHeights[size],
            variants({
              size,
              variant,
            }),
          )}
        >
          <ProfileImage size={profileImageSizes[size]} src={pictureUrl} />

          <span className="truncate">{fullName}</span>

          {rightComponent}
        </TooltipTrigger>
        <TooltipPortal>
          <TooltipContent>{fullName}</TooltipContent>
        </TooltipPortal>
      </Tooltip>
    );
  }

  const filteredAccounts = workspaceAccounts.filter(({ id }) =>
    accountIds.includes(id),
  );
  const isAnyLicenseAvailable = filteredAccounts.some(
    ({ license }) => !!license,
  );

  return (
    <Tooltip>
      <TooltipTrigger
        className={clsx(
          tagHeights[size],
          variants({
            size,
            variant: isAnyLicenseAvailable ? "purple" : "gray",
          }),
        )}
      >
        {/* Profile Images */}
        <div className="flex shrink-0 flex-row -space-x-1.5 rtl:space-x-reverse">
          {filteredAccounts
            .slice(0, 3)
            .map(({ id, picture_url: pictureUrl }) => (
              <ProfileImage
                key={id}
                size={profileImageSizes[size]}
                className={clsx(
                  "ring-1",
                  isAnyLicenseAvailable ? "ring-purple-50" : "ring-black-100",
                )}
                src={pictureUrl}
              />
            ))}
        </div>
        {filteredAccounts.length} accounts
        {rightComponent}
      </TooltipTrigger>

      <TooltipPortal>
        <TooltipContent className="flex flex-col">
          {filteredAccounts.map(({ full_name: fullName, id }) => (
            <span key={id}>{fullName}</span>
          ))}
        </TooltipContent>
      </TooltipPortal>
    </Tooltip>
  );
}
